import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Undo2 } from 'lucide-react';

import type {
  ClientCertificateListItem,
  ClientListItem,
} from '@/types/api/queries';

import { IconButton } from '@/components';
import { CopyValue } from '@/components/CopyValue';
import { DataTable } from '@/components/DataTable';
import { ListFilters } from '@/components/ListFilters';
import { TableActions } from '@/components/TableActions';

import { useList, useTableColumns } from '@/hooks';

import { CLIENT_CERTIFICATES_LIST_QUERY_KEY } from '@/queries';

import { centsToMoneyFormat } from '@/helpers';

import { CERTIFICATE_STATUS_LABEL_KEYS } from '@/constants';
import { DISPLAY_DATES_FORMAT } from '@/constants/dates';

import { StatusBadge } from '../StatusBadge';

import { CertificateComposition } from './CertificateComposition';
import { RefundCertificateDialog } from './RefundCertificateDialog';

type RefundConfig = {
  cashRegistersUrl: string;
  refundReasonsUrl: string;
};

type ClientCertificatesTableProps = {
  fetchUrl: string;
  byCodeUrl: string;
  clientsSearchUrl?: string;
  /** Base path of the clients section — the Buyer column links to `${clientsUrl}/${id}` */
  clientsUrl?: string;
  isBuyerColumnHidden?: boolean;
  pageSize?: number;
  refund?: RefundConfig;
  onRefundSuccess?: () => void;
};

const transformFilters = (applied: Record<string, unknown>) => {
  const { clients, code, from, till } = applied;
  const filters: Record<string, unknown> = {};

  if (code) {
    filters.code = code;
  }
  if (from || till) {
    filters.period = { from, till };
  }
  if (!clients) {
    return filters;
  }

  const client = clients as ClientListItem;

  return { ...filters, clients: [client.id] };
};

export const ClientCertificatesTable = (
  props: ClientCertificatesTableProps,
) => {
  const {
    fetchUrl,
    byCodeUrl,
    clientsSearchUrl,
    clientsUrl,
    isBuyerColumnHidden,
    pageSize = 50,
    refund,
    onRefundSuccess: propOnRefundSuccess,
  } = props;
  const { t } = useTranslation();

  const [refundTarget, setRefundTarget] =
    useState<ClientCertificateListItem | null>(null);

  const getClientLabel = useCallback((option: unknown) => {
    const client = option as ClientListItem;
    return `${client.first_name} ${client.last_name}`;
  }, []);

  const getClientValue = useCallback((option: unknown) => {
    const client = option as ClientListItem;
    return String(client.id);
  }, []);

  const transformClientsResponse = useCallback(
    (response: unknown) => (response as { data: ClientListItem[] }).data,
    [],
  );

  const { data, isLoading, table, filters, refetch } =
    useList<ClientCertificateListItem>({
      fetch: { url: fetchUrl, queryKey: CLIENT_CERTIFICATES_LIST_QUERY_KEY },
      pagination: { pageSize },
      sorting: { id: 'purchased_at', desc: true },
      filters: {
        transform: transformFilters,
        definitions: [
          {
            type: 'text',
            name: 'code',
            label: t('Code'),
            placeholder: t('Search by code'),
          },
          ...(clientsSearchUrl
            ? [
                {
                  type: 'autocomplete' as const,
                  name: 'clients',
                  label: t('Client'),
                  options: [],
                  fetchUrl: clientsSearchUrl,
                  queryKey: ['clients_filter', 'client_certificates'],
                  transformResponse: transformClientsResponse,
                  getOptionLabel: getClientLabel,
                  getOptionValue: getClientValue,
                },
              ]
            : []),
          { type: 'date', name: 'from', label: t('Sold from') },
          { type: 'date', name: 'till', label: t('Sold till') },
        ],
      },
    });

  const onRefundSuccess = useCallback(() => {
    refetch();
    propOnRefundSuccess?.();
  }, [refetch, propOnRefundSuccess]);

  const columns = useTableColumns<ClientCertificateListItem>(
    ({ sortable, display }) => {
      const buyerColumns = isBuyerColumnHidden
        ? []
        : [
            display('client', t('Buyer'), (row) =>
              clientsUrl ? (
                <a
                  href={`${clientsUrl}/${row.client.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium hover:underline"
                >
                  {row.client.name}
                </a>
              ) : (
                row.client.name
              ),
            ),
          ];

      const baseColumns = [
        display('code', t('Code'), (row) => <CopyValue value={row.code} />),
        ...buyerColumns,
        display('status', t('Status'), (row) => (
          <StatusBadge
            size="sm"
            variant={row.status === 'active' ? 'success' : 'neutral'}
          >
            {t(CERTIFICATE_STATUS_LABEL_KEYS[row.status])}
          </StatusBadge>
        )),
        display('sale_location', t('Sale location'), (row) => (
          <span>{row.sale_location.name}</span>
        )),
        sortable('purchased_at', t('Sold at'), {
          format: 'date',
        }),
        display('expires_at', t('Valid until'), (row) =>
          row.expires_at
            ? format(parseISO(row.expires_at), DISPLAY_DATES_FORMAT)
            : t('No expiry'),
        ),
        display('price_cents', t('Face value'), (row) =>
          centsToMoneyFormat(row.price_cents),
        ),
        display('liability_balance_cents', t('Remainder'), (row) => (
          <span className="font-semibold">
            {centsToMoneyFormat(row.liability_balance_cents)}
          </span>
        )),
      ];

      if (!refund) {
        return baseColumns;
      }

      return [
        ...baseColumns,
        display('actions', t('Actions'), (row) => (
          <TableActions>
            <IconButton
              label={t('Refund the remainder')}
              variant="destructive"
              size="icon-sm"
              disabled={!row.refundable}
              onClick={() => setRefundTarget(row)}
            >
              <Undo2 className="size-4" />
            </IconButton>
          </TableActions>
        )),
      ];
    },
  );

  const renderSubRow = useCallback(
    (row: ClientCertificateListItem) => (
      <CertificateComposition
        certificate={row}
        byCodeUrl={byCodeUrl}
      />
    ),
    [byCodeUrl],
  );

  return (
    <>
      <div className="flex flex-col gap-3">
        <ListFilters {...filters} />

        <DataTable
          data={data}
          columns={columns}
          pending={isLoading}
          renderSubRow={renderSubRow}
          {...table}
        />
      </div>

      {!!refund && (
        <RefundCertificateDialog
          isOpen={refundTarget !== null}
          certificate={refundTarget}
          cashRegistersUrl={refund.cashRegistersUrl}
          refundReasonsUrl={refund.refundReasonsUrl}
          onSuccess={onRefundSuccess}
          onClose={() => setRefundTarget(null)}
        />
      )}
    </>
  );
};
